<template>
  <div>
    <v-card class="sf-card"
            flat>
      <div class="sf-card__header"
           v-if="!hideTitle">
        <slot name="toolbar">
          <h2 class="sf-card__title">{{ title }}</h2>
          <div class="sf-card__tools">
            <v-text-field class="sf-search sf-list-card__search"
                          dense
                          flat
                          hide-details
                          prepend-inner-icon="ti-search"
                          solo
                          v-bind:placeholder="$t('search')"
                          v-if="!hideSearch"
                          v-model="search" />
            <slot name="filter"> </slot>
            <v-menu offset-y
                    left
                    v-bind:close-on-content-click="false"
                    v-bind:disabled="itemAddRouteName != null"
                    v-if="showRemoveButton"
                    v-model="newItemMenu">
              <template v-slot:activator="{ on }">
                <v-btn color="primary"
                       depressed
                       v-bind:to="itemAddRouteName != null ? { name: itemAddRouteName } : null"
                       v-on="on">
                  <v-icon left>ti-plus</v-icon>
                  {{ $t('add') }}
                </v-btn>
              </template>
              <v-card flat
                      width="420">
                <v-form ref="newItemForm">
                  <v-card-text class="pb-2">
                    <v-text-field class="mb-3"
                                  hide-details="auto"
                                  outlined
                                  v-bind:label="field.title"
                                  v-bind:key="`${field.name}-text_field`"
                                  v-bind:rules="field.isRequired ? [ rules.required ] : []"
                                  v-for="field in newItemFields"
                                  v-model="newItem[field.name]" />
                  </v-card-text>
                  <v-card-actions class="pt-0">
                    <v-spacer />
                    <v-btn color="primary"
                           depressed
                           v-on:click="addItem">
                      {{ $t('add') }}
                    </v-btn>
                  </v-card-actions>
                </v-form>
              </v-card>
            </v-menu>
          </div>
        </slot>
      </div>
      <div class="sf-card__tabs"
           v-if="extendedToolbar">
        <slot name="toolbar-extension">
          <v-tabs class="sf-tabs"
                  show-arrows
                  v-if="filter != null"
                  v-model="activeFilterValue">
            <v-tab v-bind:key="value.name"
                   v-for="value in filter.values">
              <v-icon left
                      small
                      v-if="value.icon != null">
                {{ value.icon }}
              </v-icon>
              {{ value.title }}
              <v-chip class="ml-2 sf-pill sf-pill--neutral"
                      x-small>
                {{ itemCountForFilterValue(value.name) }}
              </v-chip>
            </v-tab>
          </v-tabs>
        </slot>
      </div>
      <v-progress-linear color="accent"
                         indeterminate
                         v-bind:active="loading" />
      <v-list class="sf-list"
              v-bind:two-line="twoLine"
              v-if="filteredItems.length > 0">
        <v-data-iterator v-bind:footer-props="{ disablePagination: showAll, disableItemsPerPage: showAll, itemsPerPageOptions: [ 5, 10, 30, 50, 100, -1 ] }"
                         v-bind:items="filteredItems"
                         v-bind:items-per-page="showAll ? -1 : 30"
                         v-bind:disable-pagination="showAll"
                         v-bind:page.sync="page">

          <template v-slot:default="props">
            <template v-for="(item, index) in props.items">
              <v-list-item v-bind:id="item.id"
                          v-bind:to="itemRouteName != null ? { name: itemRouteName, params: { id: item.id } } : null"
                          v-bind:key="`${item.id}-list_item`"
                          v-bind:class="item.grey ? [ 'sf-list__item--highlighted' ] : []">
                <slot name="item-prepend"
                      v-bind:item="item">
                  <v-list-item-avatar v-if="avatar && item.icon">
                    <v-img v-bind:src="item.icon" />
                  </v-list-item-avatar>
                  <v-list-item-avatar class="sf-avatar"
                                      v-else-if="avatar && item.code">
                    <span>{{ item.code }}</span>
                  </v-list-item-avatar>
                </slot>
                <v-list-item-content>
                  <v-list-item-title>{{ item.title | defaultValue($t('untitled')) }}</v-list-item-title>
                  <v-list-item-subtitle v-if="item.subtitle">{{ item.subtitle }}</v-list-item-subtitle>
                </v-list-item-content>
                <slot name="item-append"
                      v-bind:item="item" />
                <v-list-item-action v-if="itemEditRouteName != null">
                  <v-btn icon
                         v-bind:title="$t('edit')"
                         v-bind:to="{ name: itemEditRouteName, params: { id: item.id } }">
                    <v-icon>ti-pencil</v-icon>
                  </v-btn>
                </v-list-item-action>
                <v-list-item-action v-if="showRemoveButton">
                  <v-menu left
                          offset-y>
                    <template v-slot:activator="{ on, value }">
                      <v-btn icon
                             v-bind:disabled="value"
                             v-bind:title="$t('remove')"
                             v-if="!item.undeletable"
                             v-on="on">
                        <v-icon>ti-trash</v-icon>
                      </v-btn>
                    </template>
                    <v-card flat>
                      <v-card-actions>
                        <v-btn color="error"
                               depressed
                               v-on:click="removeItem(item.id)">
                          <v-icon left>ti-trash</v-icon>
                          {{ $t('confirmRemove') }}
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-menu>
                </v-list-item-action>
              </v-list-item>
              <v-divider v-bind:key="`${item.id}-divider`"
                         v-if="index < props.items.length - 1" />
            </template>
            <v-divider />
          </template>
        </v-data-iterator>
      </v-list>
      <v-card-text class="sf-card__body sf-muted"
                   v-else-if="!loading">
        {{ $t('noItemsWereFound') }}
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
  export default {
    computed: {
      filteredItems() {
        let items = this.items

        if (this.filter != null && this.activeFilterValue != null) {
          const activeFilterValue = this.filter.values[this.activeFilterValue]

          if (!activeFilterValue.showAll) {

            if (activeFilterValue.name === 'on-site') {
              items = items.filter(i => i.wasAttending)
            } else {
              items = items.filter(i => i.state === activeFilterValue.name)
            }


          }
        }

        if (this.search != null && this.search.count !== 0) {
          const search = this.search.toLowerCase()

          items = items.filter(i => i.title.toLowerCase().includes(search))
        }

        return items
      },
      rules() {
        return {
          required: v => !!v || this.$t('global.required')
        }
      }
    },
    data: () => ({
      activeFilterValue: null,
      newItem: {},
      newItemMenu: false,
      search: null,
      page: null
    }),
    methods: {
      addItem() {
        if (!this.$refs.newItemForm.validate()) {
          return
        }

        this.newItemMenu = false

        this.newItem.icon = '/images/icons/Keine_Angaben.png'

        this.$emit('add-item', this.newItem)

        this.newItem = {}
        this.$refs.newItemForm.reset()
      },
      itemCountForFilterValue(name) {
        const filterValue = this.filter.values.find(v => v.name === name)

        if (filterValue == null || filterValue.showAll) {
          return this.items.length
        }

        if (name === 'on-site') {
          return this.items.filter(i => i.wasAttending).length
        }

        return this.items.filter(i => i.state === name).length
      },
      removeItem(id) {
        this.$emit('remove-item', id)
      }
    },
    name: 'ListCard',
    props: {
      avatar: Boolean,
      extendedToolbar: Boolean,
      filter: Object,
      hideSearch: Boolean,
      itemAddRouteName: String,
      itemEditRouteName: String,
      itemRouteName: String,
      items: Array,
      loading: Boolean,
      newItemFields: Array,
      showAddButton: {
        default: false,
        type: Boolean
      },
      showRemoveButton: {
        default: false,
        type: Boolean
      },
      title: String,
      twoLine: Boolean,
      hideTitle: Boolean,
      showAll: Boolean
    }
  }
</script>

<style scoped>
  .sf-list-card__search {
    flex: 1 1 200px;
    max-width: 320px;
  }

</style>

<i18n>
  {
    "de": {
      "add": "Hinzufügen",
      "confirmRemove": "Löschen bestätigen",
      "noItemsWereFound": "Es sind keine Einträge vorhanden.",
      "untitled": "Unbenannt",
      "search": "Suchen",
      "edit": "Bearbeiten",
      "remove": "Löschen"
    },
    "en": {
      "add": "Add",
      "confirmRemove": "Confirm remove",
      "noItemsWereFound": "No items were found.",
      "untitled": "Untitled",
      "search": "Search",
      "edit": "Edit",
      "remove": "Remove"
    }
  }
</i18n>